'use client';

import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { colors, baseStyles, formatNum } from './SharedUI';
import { useBinanceStream } from './useBinanceStream';


const formatSmartPrice = (val) => {
  if (!val) return '$0';
  const num = parseFloat(val);
  if (num >= 1000) return `$${formatNum(num, 2)}`;
  if (num >= 1) return `$${formatNum(num, 4)}`;
  if (num >= 0.001) return `$${formatNum(num, 6)}`;
  return `$${formatNum(num, 8)}`;
};

const formatUSD = (val) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
  return `$${val.toFixed(0)}`;
};

// ==========================================
// STATIC DATA 1: 12 CASE CHIẾN THUẬT SỔ LỆNH VÀ CVD
// ==========================================
const MARKET_CASES = [
  { id: 1, name: "Rút củi đáy nồi", desc: "Bids giảm >15%", probability: 75, side: "DUMP", color: colors.red, category: "INTENT" },
  { id: 2, name: "Tường giả đè giá", desc: "Spoofing Asks lớn", probability: 65, side: "DUMP", color: colors.red, category: "INTENT" },
  { id: 6, name: "Bơm nền móng", desc: "Asks giảm >15%", probability: 75, side: "PUMP", color: colors.green, category: "INTENT" },
  { id: 7, name: "Tường giả đỡ giá", desc: "Spoofing Bids lớn", probability: 65, side: "PUMP", color: colors.green, category: "INTENT" },
  { id: 12, name: "Giãn sổ lệnh", desc: "Spread rộng, rỗng ruột", probability: 60, side: "NEUTRAL", color: colors.warn, category: "INTENT" },
  
  { id: 3, name: "Phân kỳ CVD Đỉnh", desc: "Giá ngang, CVD âm", probability: 70, side: "DUMP", color: colors.red, category: "ACTION" },
  { id: 4, name: "Tốc độ xả vọt", desc: "Market Sell nã liên tục", probability: 80, side: "DUMP", color: colors.red, category: "ACTION" },
  { id: 8, name: "Phân kỳ CVD Đáy", desc: "Giá ngang, CVD dương", probability: 70, side: "PUMP", color: colors.green, category: "ACTION" },
  { id: 9, name: "Tốc độ gom vọt", desc: "Market Buy nã liên tục", probability: 80, side: "PUMP", color: colors.green, category: "ACTION" },
  { id: 11, name: "Hấp thụ lực mua/bán", desc: "CVD lớn nhưng giá kẹt", probability: 75, side: "NEUTRAL", color: colors.warn, category: "ACTION" },
  
  { id: 5, name: "Chạm Kháng Cự", desc: "Hợp lưu cản V3 / Fakeout đỉnh", probability: 85, side: "DUMP", color: colors.red, category: "CONFIRMATION" },
  { id: 10, name: "Chạm Hỗ Trợ", desc: "Hợp lưu đỡ V3 / Fakeout đáy", probability: 85, side: "PUMP", color: colors.green, category: "CONFIRMATION" }
];

// ==========================================
// STATIC DATA 2: 5 COMBO MARKET MAKER
// ==========================================
const MM_COMBOS = [
  { id: 1, name: "GOM BẮT ĐÁY", desc: "Hứng Thật + Dọa Đè Ảo", formula: "(Bid Thật > Ask Thật) + (Ask Ảo > Bid Ảo)", side: "LONG", color: colors.green, icon: "🟢" },
  { id: 2, name: "PHÂN PHỐI ĐỈNH", desc: "Xả Thật + Đỡ Dưới Ảo", formula: "(Ask Thật > Bid Thật) + (Bid Ảo > Ask Ảo)", side: "SHORT", color: colors.red, icon: "🔴" },
  { id: 3, name: "ĐẨY GIÁ FOMO", desc: "Mua Thật + Kê Đít Ảo", formula: "(Bid Thật > Ask Thật) + (Bid Ảo > Ask Ảo)", side: "LONG", color: colors.green, icon: "🚀" },
  { id: 4, name: "ĐẠP PANIC SELL", desc: "Bán Thật + Kê Đầu Ảo", formula: "(Ask Thật > Bid Thật) + (Ask Ảo > Bid Ảo)", side: "SHORT", color: colors.red, icon: "🩸" },
  { id: 5, name: "GIẰNG CO / NHIỄU", desc: "Điểm Thật/Ảo nhảy loạn ngẫu nhiên", formula: "Các thông số xấp xỉ nhau", side: "NEUTRAL", color: colors.textSub, icon: "⚖️" }
];

const RealtimeDumpRadar = forwardRef(({ symbol = 'BTCUSDT', macroZones = null }, ref) => {
  const [targetSymbol, setTargetSymbol] = useState(symbol.toUpperCase());
  const [isStreaming, setIsStreaming] = useState(false);

  // [MỚI] Gọi thêm reconnectSocket từ Hook
  const { streamData, wsStatus, resetCVD, reconnectSocket } = useBinanceStream(targetSymbol.toLowerCase(), isStreaming);

  const [usdThreshold, setUsdThreshold] = useState(50000);
  const [draftThreshold, setDraftThreshold] = useState(50000);
  const [imbalanceBaseline, setImbalanceBaseline] = useState(3);
  const [draftBaseline, setDraftBaseline] = useState(3);
  const [baselinePrice, setBaselinePrice] = useState(0);
  const [draftBaselinePrice, setDraftBaselinePrice] = useState(0);

  const [boostMetrics, setBoostMetrics] = useState({ dump: 0, pump: 0 });

  const configRef = useRef({
      usdThreshold: 50000,
      imbalanceBaseline: 3,
      baselinePrice: 0 
  });

  const [metrics, setMetrics] = useState({
    price: 0, totalBids: 0, totalAsks: 0, cvd: 0, imbalancePct: 0,
    status: 'ĐANG CHỜ DATA...', statusColor: colors.textSub,
    overallScore: 0, overallSide: 'NEUTRAL'
  });

  const [towMetrics, setTowMetrics] = useState({
    asksFake: 0, asksReal: 0, bidsFake: 0, bidsReal: 0, 
    icebergLongs: 0, icebergShorts: 0,
    totalLongPower: 0, totalShortPower: 0, activeComboId: 5,
    currentPrice: 0, baseP: 0, priceDiff: 0
  });

  const [activeCaseIds, setActiveCaseIds] = useState([]);
  const [intentHistory, setIntentHistory] = useState([]); 
  const [actionHistory, setActionHistory] = useState([]); 

  const updateIntervalRef = useRef(null);
  const lastRenderRef = useRef(Date.now()); 

  const streamDataRef = useRef({
    price: 0, cvd: 0, prevCvd: 0,
    prevBidsQty: 0, prevAsksQty: 0,
    bidsMap: new Map(), asksMap: new Map(),
    tugOfWar: { asksFake: 0, asksReal: 0, bidsFake: 0, bidsReal: 0 },
    absorption: { icebergShorts: 0, icebergLongs: 0 },
    lastIntentKey: '', lastActionKey: '', boostCounts: { dump: 0, pump: 0 }
  });

  const latestTowRef = useRef({ towMetrics: null });

  useEffect(() => {
    latestTowRef.current = { towMetrics };
  }, [towMetrics]);

  useImperativeHandle(ref, () => ({
    getSnapshot: () => latestTowRef.current
  }));


  useEffect(() => {
    if (symbol) {
      const newSymbol = symbol.toUpperCase();
      setTargetSymbol(newSymbol);
      if (isStreaming) startSniper(newSymbol);
    }
  }, [symbol]);

  const softResetCounters = () => {
    streamDataRef.current.tugOfWar = { asksFake: 0, asksReal: 0, bidsFake: 0, bidsReal: 0 };
    streamDataRef.current.absorption = { icebergShorts: 0, icebergLongs: 0 };
    streamDataRef.current.boostCounts = { dump: 0, pump: 0 };
    streamDataRef.current.lastIntentKey = '';
    streamDataRef.current.lastActionKey = '';
    
    setTowMetrics(prev => ({ 
        ...prev, asksFake: 0, asksReal: 0, bidsFake: 0, bidsReal: 0, 
        icebergLongs: 0, icebergShorts: 0,
        totalLongPower: 0, totalShortPower: 0, activeComboId: 5 
    }));
    setBoostMetrics({ dump: 0, pump: 0 });
    setIntentHistory([]);
    setActionHistory([]);
  };

  const handleSaveThreshold = () => {
    const newVal = Number(draftThreshold);
    if (!isNaN(newVal) && newVal >= 0) {
      setUsdThreshold(newVal); configRef.current.usdThreshold = newVal;
      softResetCounters();
    }
  };

  const handleSaveBaseline = () => {
    const newVal = Number(draftBaseline);
    if (!isNaN(newVal) && newVal > 0) {
      setImbalanceBaseline(newVal); configRef.current.imbalanceBaseline = newVal;
    }
  };

  const handleSaveBaselinePrice = () => {
    const newVal = Number(draftBaselinePrice);
    if (!isNaN(newVal) && newVal > 0) {
      setBaselinePrice(newVal); configRef.current.baselinePrice = newVal;
      softResetCounters();
    }
  };

  const handleResetTow = () => {
      softResetCounters();
  };

  // ==========================================
  // LẮNG NGHE NÚT RESET SOCKET TỪ UI
  // ==========================================
  const handleForceResetSocket = () => {
    if (!isStreaming) return;
    reconnectSocket();
    softResetCounters();
    setMetrics(prev => ({
        ...prev, 
        price: 0, totalBids: 0, totalAsks: 0, cvd: 0, imbalancePct: 0,
        status: '🔄 ĐANG KHỞI ĐỘNG LẠI MẠNG...', statusColor: colors.warn
    }));
  };

  const runDetection = useCallback((currMetrics) => {
    const data = streamDataRef.current;
    const cfg = configRef.current; 
    let detected = [];
    
    let dumpScore = 0; let dumpCount = 0;
    let pumpScore = 0; let pumpCount = 0;

    const bidDrop = data.prevBidsQty > 0 && currMetrics.totalBids < data.prevBidsQty * 0.85;
    const askDrop = data.prevAsksQty > 0 && currMetrics.totalAsks < data.prevAsksQty * 0.85;
    const cvdChange = currMetrics.cvd - data.prevCvd;

    const baseline = cfg.imbalanceBaseline;
    const imbPct = currMetrics.imbalancePct;

    if (bidDrop) { detected.push(1); dumpScore += 75; dumpCount++; }
    if (imbPct >= baseline) { detected.push(2); dumpScore += 65; dumpCount++; }
    if (cvdChange < -5) { detected.push(4); dumpScore += 80; dumpCount++; }
    if (Math.abs(cvdChange) < 1 && imbPct >= 2) { detected.push(3); dumpScore += 70; dumpCount++; } 
    
    if (askDrop) { detected.push(6); pumpScore += 75; pumpCount++; }
    if (currMetrics.totalAsks > 0 && (currMetrics.totalBids / currMetrics.totalAsks) >= baseline) { detected.push(7); pumpScore += 65; pumpCount++; }
    if (cvdChange > 5) { detected.push(9); pumpScore += 80; pumpCount++; }
    if (Math.abs(cvdChange) < 1 && (currMetrics.totalBids / (currMetrics.totalAsks||1)) >= 2) { detected.push(8); pumpScore += 70; pumpCount++; }
    
    if (Math.abs(cvdChange) > 10 && Math.abs(currMetrics.price - data.price) < (data.price * 0.0001)) { detected.push(11); }
    if (currMetrics.totalBids < data.prevBidsQty * 0.5 && currMetrics.totalAsks < data.prevAsksQty * 0.5) { detected.push(12); }

    if (macroZones && currMetrics.price > 0) {
        if (currMetrics.price >= macroZones.sell.lower) {
            detected.push(5); dumpScore += 85; dumpCount++;
        }
        if (currMetrics.price <= macroZones.buy.upper) {
            detected.push(10); pumpScore += 85; pumpCount++;
        }
    }

    setActiveCaseIds(detected);

    let currentSide = 'NEUTRAL';
    let finalScore = 0;
    if (dumpScore > pumpScore) { currentSide = 'DUMP'; finalScore = dumpScore / dumpCount; }
    else if (pumpScore > dumpScore) { currentSide = 'PUMP'; finalScore = pumpScore / pumpCount; }

    const time = new Date().toLocaleTimeString('vi-VN');
    const intentCases = detected.filter(id => MARKET_CASES.find(c => c.id === id)?.category === 'INTENT');
    const actionConfCases = detected.filter(id => ['ACTION', 'CONFIRMATION'].includes(MARKET_CASES.find(c => c.id === id)?.category));

    const calculateSpecificScore = (caseArray, isActionGroup) => {
        let dScore = 0, dCount = 0, pScore = 0, pCount = 0;
        caseArray.forEach(id => {
            const c = MARKET_CASES.find(x => x.id === id);
            if (c.side === 'DUMP') { dScore += c.probability; dCount++; }
            if (c.side === 'PUMP') { pScore += c.probability; pCount++; }
        });

        let boost = 0;
        let note = "";
        if (isActionGroup) {
            if (imbPct >= baseline * 1.5) { 
                boost = 15; note = " (+Lực Đè Xác Nhận)";
            } else if (imbPct > 0 && imbPct <= (1 / (baseline * 1.5))) { 
                boost = 15; note = " (+Lực Đỡ Xác Nhận)";
            }
        }

        if (dScore > pScore) return { side: 'DUMP', score: Math.min(99, (dScore / dCount) + (imbPct >= baseline * 1.5 ? boost : 0)).toFixed(0), note: imbPct >= baseline * 1.5 ? note : "" };
        if (pScore > dScore) return { side: 'PUMP', score: Math.min(99, (pScore / pCount) + (imbPct > 0 && imbPct <= (1 / (baseline * 1.5)) ? boost : 0)).toFixed(0), note: (imbPct > 0 && imbPct <= (1 / (baseline * 1.5))) ? note : "" };
        return { side: 'NEUTRAL', score: 0, note: "" };
    };

    if (intentCases.length > 0) {
        const intentStats = calculateSpecificScore(intentCases, false);
        const intentKey = intentCases.join(',') + '_' + intentStats.side;
        
        if (data.lastIntentKey !== intentKey) {
            data.lastIntentKey = intentKey;
            setIntentHistory(prev => [{ time, cases: intentCases, price: currMetrics.price, score: intentStats.score, side: intentStats.side, note: "" }, ...prev].slice(0, 30));
        }
    }

    if (actionConfCases.length > 0) {
        const actionStats = calculateSpecificScore(actionConfCases, true);
        const actionKey = actionConfCases.join(',') + '_' + actionStats.side;
        
        if (data.lastActionKey !== actionKey) {
            data.lastActionKey = actionKey;
            
            if (actionStats.note.includes('Đè')) {
                data.boostCounts.dump += 1;
            } else if (actionStats.note.includes('Đỡ')) {
                data.boostCounts.pump += 1;
            }
            setBoostMetrics({ ...data.boostCounts });

            setActionHistory(prev => [{ time, cases: actionConfCases, price: currMetrics.price, score: actionStats.score, side: actionStats.side, note: actionStats.note }, ...prev].slice(0, 30));
        }
    }
    
    return { score: finalScore, side: currentSide };
  }, [macroZones]);

  const calculateMetrics = () => {
    if (!streamData.price || streamData.bids.length === 0 || streamData.asks.length === 0) return;
    
    const data = streamDataRef.current;
    const cfg = configRef.current; 
    const currentPrice = streamData.price;
    const currentCvd = streamData.cvd;
  
    if (cfg.baselinePrice === 0 && currentPrice > 0) {
        cfg.baselinePrice = currentPrice;
        setBaselinePrice(currentPrice);
        setDraftBaselinePrice(currentPrice);
    }
  
    const currentBidsMap = new Map();
    let totalBidQty = 0;
    streamData.bids.forEach(b => {
        const p = parseFloat(b[0]); const q = parseFloat(b[1]);
        currentBidsMap.set(p, q);
        totalBidQty += q;
    });
  
    const currentAsksMap = new Map();
    let totalAskQty = 0;
    streamData.asks.forEach(a => {
        const p = parseFloat(a[0]); const q = parseFloat(a[1]);
        currentAsksMap.set(p, q);
        totalAskQty += q;
    });
  
    let deltaCvdUsd = data.prevCvd !== 0 ? (currentCvd - data.prevCvd) * currentPrice : 0;
    let marketBuysUsd = deltaCvdUsd > 0 ? deltaCvdUsd : 0;
    let marketSellsUsd = deltaCvdUsd < 0 ? Math.abs(deltaCvdUsd) : 0;
  
    let tickDeltaBidsUsd = 0; 
    let tickDeltaAsksUsd = 0; 
  
    data.asksMap.forEach((prevQty, priceLevel) => {
        const currQty = currentAsksMap.get(priceLevel) || 0;
        if (prevQty > currQty) {
            tickDeltaAsksUsd += (prevQty - currQty) * priceLevel; 
        }
    });
  
    data.bidsMap.forEach((prevQty, priceLevel) => {
        const currQty = currentBidsMap.get(priceLevel) || 0;
        if (prevQty > currQty) {
            tickDeltaBidsUsd += (prevQty - currQty) * priceLevel; 
        }
    });
  
    const threshold = cfg.usdThreshold;
    let tow = { ...data.tugOfWar };
    let abs = { ...data.absorption };
  
    if (marketBuysUsd > 0) {
        let realAsk = Math.min(tickDeltaAsksUsd, marketBuysUsd);
        let fakeAsk = Math.max(0, tickDeltaAsksUsd - realAsk); 
        let icebergShortVolume = Math.max(0, marketBuysUsd - tickDeltaAsksUsd);
  
        if (fakeAsk > threshold) tow.asksFake += fakeAsk;
        if (realAsk > threshold) tow.asksReal += realAsk;
        if (icebergShortVolume > threshold) abs.icebergShorts += icebergShortVolume; 
    } else {
        if (tickDeltaAsksUsd > threshold) tow.asksFake += tickDeltaAsksUsd;
    }
  
    if (marketSellsUsd > 0) {
        let realBid = Math.min(tickDeltaBidsUsd, marketSellsUsd);
        let fakeBid = Math.max(0, tickDeltaBidsUsd - realBid);
        let icebergLongVolume = Math.max(0, marketSellsUsd - tickDeltaBidsUsd);
  
        if (fakeBid > threshold) tow.bidsFake += fakeBid;
        if (realBid > threshold) tow.bidsReal += realBid;
        if (icebergLongVolume > threshold) abs.icebergLongs += icebergLongVolume; 
    } else {
        if (tickDeltaBidsUsd > threshold) tow.bidsFake += tickDeltaBidsUsd;
    }
  
    data.bidsMap = currentBidsMap;
    data.asksMap = currentAsksMap;
    data.price = currentPrice;
    data.cvd = currentCvd;
    data.tugOfWar = tow;
    data.absorption = abs;
  
    const now = Date.now();
    if (now - lastRenderRef.current >= 1000) {
        let imbalance = totalBidQty > 0 ? (totalAskQty / totalBidQty) : 0;
        let totalLongPower = tow.bidsReal + tow.asksFake + abs.icebergLongs; 
        let totalShortPower = tow.asksReal + tow.bidsFake + abs.icebergShorts;
        
        let activeComboId = 5; 
        if ((totalLongPower + totalShortPower) > threshold) {
            const isBidRealDom = tow.bidsReal > tow.asksReal;
            const isAskRealDom = tow.asksReal > tow.bidsReal;
            const isAskFakeDom = tow.asksFake > tow.bidsFake;
            const isBidFakeDom = tow.bidsFake > tow.asksFake;
  
            if (isBidRealDom && isAskFakeDom) activeComboId = 1;      
            else if (isAskRealDom && isBidFakeDom) activeComboId = 2; 
            else if (isBidRealDom && isBidFakeDom) activeComboId = 3; 
            else if (isAskRealDom && isAskFakeDom) activeComboId = 4; 
        }
  
        const baseP = cfg.baselinePrice;
        const priceDiff = baseP > 0 ? ((currentPrice - baseP) / baseP) * 100 : 0;
  
        const currMetrics = { price: currentPrice, totalBids: totalBidQty, totalAsks: totalAskQty, cvd: currentCvd, imbalancePct: imbalance };
        const prediction = runDetection(currMetrics);
        
        data.prevBidsQty = totalBidQty;
        data.prevAsksQty = totalAskQty;
        data.prevCvd = currentCvd;
  
        let currentStatus = "CÂN BẰNG THUẬN TỰ NHIÊN";
        let color = colors.textMain;
        if (prediction.side === 'DUMP' && prediction.score > 60) {
            currentStatus = `🚨 DUMP ALERT: ÁP LỰC BÁN CAO (${prediction.score}%)`; color = colors.red;
        } else if (prediction.side === 'PUMP' && prediction.score > 60) {
            currentStatus = `🚀 PUMP ALERT: LỰC GOM MẠNH (${prediction.score}%)`; color = colors.green;
        }
  
        setMetrics({ ...currMetrics, status: currentStatus, statusColor: color, overallScore: prediction.score, overallSide: prediction.side });
        
        setTowMetrics({ 
            ...tow, totalLongPower, totalShortPower, activeComboId,
            currentPrice, baseP, priceDiff,
            icebergLongs: abs.icebergLongs, icebergShorts: abs.icebergShorts
        });
  
        lastRenderRef.current = now; 
    }
  };

  const startSniper = (overrideSymbol = null) => {
    const symbolToScan = overrideSymbol || targetSymbol;
    if (!symbolToScan) return;
    
    setTargetSymbol(symbolToScan.toUpperCase());
    setIsStreaming(true);
    
    configRef.current.baselinePrice = 0;
    setBaselinePrice(0);
    setDraftBaselinePrice(0);

    softResetCounters();
    resetCVD(); 

    streamDataRef.current = { 
      price: 0, cvd: 0, prevCvd: 0,
      prevBidsQty: 0, prevAsksQty: 0,
      bidsMap: new Map(), asksMap: new Map(),
      tugOfWar: { asksFake: 0, asksReal: 0, bidsFake: 0, bidsReal: 0 },
      absorption: { icebergShorts: 0, icebergLongs: 0 },
      lastIntentKey: '', lastActionKey: '', boostCounts: { dump: 0, pump: 0 }
    };
    
    setMetrics({
      price: 0, totalBids: 0, totalAsks: 0, cvd: 0, imbalancePct: 0,
      status: 'ĐANG CHỜ DATA...', statusColor: colors.warn,
      overallScore: 0, overallSide: 'NEUTRAL'
    });

    if (updateIntervalRef.current) clearInterval(updateIntervalRef.current);
    updateIntervalRef.current = setInterval(calculateMetrics, 250);
  };

  const stopSniper = useCallback(() => {
    setIsStreaming(false); 
    if (updateIntervalRef.current) clearInterval(updateIntervalRef.current);
    
    setMetrics(prev => ({ ...prev, status: '⏸ ĐÃ DỪNG (DỮ LIỆU ĐÓNG BĂNG)', statusColor: colors.textSub }));
    setActiveCaseIds([]);
  }, []);

  const renderCategoryColumn = (title, icon, categoryType) => {
    return (
      <div style={{ flex: 1, minWidth: '220px', backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: '13px', color: colors.textMain, fontWeight: 'bold', marginBottom: '12px', textAlign: 'center', borderBottom: `1px dashed ${colors.border}`, paddingBottom: '8px' }}>
          {icon} {title}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {MARKET_CASES.filter(c => c.category === categoryType).map(c => {
            const isActive = activeCaseIds.includes(c.id);
            const caseIcon = c.side === 'DUMP' ? '🩸' : c.side === 'PUMP' ? '🚀' : '⚡';

            return (
              <div key={c.id} style={{ 
                padding: '8px', 
                borderRadius: '6px', 
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: isActive ? c.color : 'rgba(255,255,255,0.05)',
                boxSizing: 'border-box',
                backgroundColor: isActive ? `${c.color}20` : 'rgba(255,255,255,0.01)',
                transition: 'background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease', 
                opacity: isActive ? 1 : 0.4
              }}>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: isActive ? c.color : colors.textSub, display: 'flex', alignItems: 'center' }}>
                  <span style={{ width: '20px', display: 'inline-block', textAlign: 'left', opacity: isActive ? 1 : 0.3, transition: 'opacity 0.2s ease' }}>
                      {caseIcon}
                  </span>
                  <span>{c.name}</span>
                </div>
                <div style={{ fontSize: '10px', color: isActive ? '#fff' : colors.textSub, marginTop: '4px', paddingLeft: '20px', lineHeight: '1.2' }}>
                  {c.desc}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderHistoryBox = (title, listData, emptyMsg, extraUI = null) => {
    return (
      <div style={{ flex: 1, minWidth: '300px', backgroundColor: 'rgba(0,0,0,0.2)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
        <div style={{ fontSize: '13px', color: colors.textMain, fontWeight: 'bold', marginBottom: '12px' }}>{title}</div>
        
        {extraUI}

        <div style={{ maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '4px' }}>
          {listData.length === 0 ? (
              <div style={{ fontSize: '12px', color: colors.textSub, fontStyle: 'italic', padding: '15px', textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.01)', borderRadius: '6px' }}>
                  {emptyMsg}
              </div>
          ) : 
            listData.map((h, i) => {
              const isDump = h.side === 'DUMP';
              const isNeutral = h.side === 'NEUTRAL';
              const logColor = isDump ? colors.red : (isNeutral ? colors.warn : colors.green);
              
              const getNamesByCategory = (cat) => h.cases.filter(id => MARKET_CASES.find(c => c.id === id)?.category === cat).map(id => MARKET_CASES.find(c => c.id === id).name);
              const intentStrs = getNamesByCategory('INTENT');
              const actionStrs = getNamesByCategory('ACTION');
              const confStrs = getNamesByCategory('CONFIRMATION');
              
              return (
              <div key={i} style={{ 
                  display: 'flex', flexDirection: 'column', gap: '6px',
                  fontSize: '11px', padding: '10px 12px', 
                  backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '6px', 
                  borderLeft: `4px solid ${logColor}`,
                  opacity: i === 0 ? 1 : 0.7 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ color: logColor, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {isDump ? '🔴' : (isNeutral ? '🟡' : '🟢')} {h.score > 0 ? `${h.score}% ` : ''}{h.side} DETECTED
                        {h.note && <span style={{ fontSize: '9px', backgroundColor: logColor, color: '#000', padding: '2px 4px', borderRadius: '4px', marginLeft: '4px' }}>{h.note}</span>}
                    </strong>
                    <span style={{ color: colors.textSub, fontFamily: 'monospace' }}>[{h.time}] • Giá: <span style={{color: '#fff'}}>{formatSmartPrice(h.price)}</span></span>
                </div>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', color: colors.textSub, fontSize: '11px' }}>
                    Đã phát hiện 
                    {intentStrs.length > 0 && <span> <span style={{color: '#fff'}}>[ Ý ĐỊNH: {intentStrs.join(', ')} ]</span> </span>}
                    {intentStrs.length > 0 && actionStrs.length > 0 && <span>+</span>}
                    {actionStrs.length > 0 && <span> <span style={{color: '#fff'}}>[ HÀNH ĐỘNG: {actionStrs.join(', ')} ]</span> </span>}
                    {(intentStrs.length > 0 || actionStrs.length > 0) && confStrs.length > 0 && <span>tại</span>}
                    {confStrs.length > 0 && <span> <span style={{color: '#fff'}}>[ XÁC NHẬN: {confStrs.join(', ')} ]</span> </span>}
                </div>
              </div>
            )})}
        </div>
      </div>
    );
  };

  const actionComparisonUI = (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '6px', marginBottom: '12px', fontSize: '11px', border: `1px dashed ${colors.border}` }}>
         <div style={{ color: colors.red }}>🩸 Đè Xác Nhận: <strong style={{ fontSize: '14px' }}>{boostMetrics.dump}</strong></div>
         <div style={{ color: colors.textMain, fontWeight: 'bold', textAlign: 'center', flex: 1 }}>
            {boostMetrics.dump > boostMetrics.pump 
              ? <span style={{ color: colors.red }}>📉 Phe Bán Áp Đảo (+{boostMetrics.dump - boostMetrics.pump})</span> 
              : (boostMetrics.pump > boostMetrics.dump 
                  ? <span style={{ color: colors.green }}>🚀 Phe Mua Áp Đảo (+{boostMetrics.pump - boostMetrics.dump})</span> 
                  : <span style={{ color: colors.warn }}>⚖️ Đang Cân Bằng</span>)}
         </div>
         <div style={{ color: colors.green }}>🛡️ Đỡ Xác Nhận: <strong style={{ fontSize: '14px' }}>{boostMetrics.pump}</strong></div>
      </div>
  );

  return (
    <div style={{ ...baseStyles.card, marginBottom: '24px', border: `1px solid ${isStreaming ? colors.warn : colors.border}`, boxSizing: 'border-box' }}>
      
      <div style={{ borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <strong style={{ color: colors.warn, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>🎯</span> HỆ THỐNG DÒ QUÉT CÁ MẬP (ORDER BOOK & CVD)
            </strong>
            {macroZones && (
              <span style={{ fontSize: '11px', backgroundColor: 'rgba(34, 197, 94, 0.1)', color: colors.green, padding: '2px 8px', borderRadius: '12px', border: `1px solid ${colors.green}` }}>
                📡 Đã đồng bộ GPS Vĩ mô (Case 5 & 10)
              </span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '6px', border: `1px solid ${colors.border}`, padding: '4px 8px', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Lọc Nhiễu ($):</span>
              <input 
                type="number" 
                value={draftThreshold} 
                onChange={(e) => setDraftThreshold(e.target.value)} 
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveThreshold(); }}
                style={{ ...baseStyles.input, width: '65px', padding: '2px 4px', fontSize: '12px', backgroundColor: 'transparent', border: 'none', color: colors.warn, fontWeight: 'bold' }}
              />
              {Number(draftThreshold) !== Number(usdThreshold) && (
                <button onClick={handleSaveThreshold} style={{ background: colors.warn, color: '#000', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>LƯU</button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '6px', border: `1px solid ${colors.border}`, padding: '4px 8px', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Baseline Đè (x):</span>
              <input 
                type="number" step="0.5"
                value={draftBaseline} 
                onChange={(e) => setDraftBaseline(e.target.value)} 
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBaseline(); }}
                style={{ ...baseStyles.input, width: '40px', padding: '2px 4px', fontSize: '12px', backgroundColor: 'transparent', border: 'none', color: colors.warn, fontWeight: 'bold' }}
              />
              {Number(draftBaseline) !== Number(imbalanceBaseline) && (
                <button onClick={handleSaveBaseline} style={{ background: colors.warn, color: '#000', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>LƯU</button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '6px', border: `1px solid ${colors.border}`, padding: '4px 8px', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Giá Mốc ($):</span>
              <input 
                type="number" step="0.0001"
                value={draftBaselinePrice} 
                onChange={(e) => setDraftBaselinePrice(e.target.value)} 
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBaselinePrice(); }}
                style={{ ...baseStyles.input, width: '70px', padding: '2px 4px', fontSize: '12px', backgroundColor: 'transparent', border: 'none', color: colors.warn, fontWeight: 'bold' }}
              />
              {Number(draftBaselinePrice) !== Number(baselinePrice) && (
                <button onClick={handleSaveBaselinePrice} style={{ background: colors.warn, color: '#000', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>LƯU</button>
              )}
            </div>

            <div style={{ backgroundColor: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', color: wsStatus.color, border: `1px solid ${wsStatus.color}`, display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isStreaming && wsStatus.text.includes('LIVE') && (
                <div style={{ width: '8px', height: '8px', backgroundColor: colors.green, borderRadius: '50%', animation: 'pulse 1.5s infinite' }}></div>
              )}
              {wsStatus.text}
            </div>

            {/* [MỚI] NÚT RESET SOCKET */}
            <button 
                onClick={handleForceResetSocket} 
                disabled={!isStreaming}
                style={{ 
                    ...baseStyles.select, 
                    backgroundColor: isStreaming ? 'rgba(245, 158, 11, 0.2)' : 'transparent', 
                    color: isStreaming ? colors.warn : colors.border, 
                    border: `1px solid ${isStreaming ? colors.warn : colors.border}`, 
                    fontWeight: 'bold', padding: '6px 10px', fontSize: '12px',
                    cursor: isStreaming ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', gap: '4px'
                }}
                title="Khởi động lại kết nối WebSocket nếu bị lag hoặc kẹt data"
            >
                🔄 RESET SOCKET
            </button>

            <input type="text" value={targetSymbol} onChange={(e) => setTargetSymbol(e.target.value.toUpperCase())} placeholder="VD: BTCUSDT" disabled={isStreaming} style={{ ...baseStyles.input, width: '100px', textAlign: 'center', fontWeight: 'bold' }} />
            {!isStreaming ? (
              <button onClick={() => startSniper()} style={{ ...baseStyles.select, backgroundColor: colors.red, color: '#fff', border: 'none', fontWeight: 'bold', padding: '8px 16px' }}>
                🔴 BẬT RADAR 
              </button>
            ) : (
              <button onClick={stopSniper} style={{ ...baseStyles.select, backgroundColor: colors.border, color: colors.textMain, border: 'none', fontWeight: 'bold', padding: '8px 16px' }}>
                ⏹ DỪNG RADAR
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', opacity: isStreaming ? 1 : 0.8, transition: 'opacity 0.3s' }}>
        
        <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', border: `2px dashed ${metrics.statusColor}`, borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
             Dự báo AI: Tình trạng Khớp Lệnh & Order Book
          </div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: metrics.statusColor }}>{metrics.status}</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: colors.textMain, marginTop: '10px' }}>
            {formatSmartPrice(metrics.price)}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: colors.textMain, marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
              <span>🧱 Tường Sổ Lệnh (20 Nấc)</span>
              <span style={{ color: metrics.imbalancePct > imbalanceBaseline ? colors.red : metrics.imbalancePct < (1/imbalanceBaseline) && metrics.imbalancePct > 0 ? colors.green : colors.textSub }}>
                Tỷ lệ đè: {metrics.imbalancePct > 0 ? `${metrics.imbalancePct.toFixed(2)}x` : '--'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: colors.red, marginBottom: '4px' }}>
                  <span>Tường Bán (Asks - Cản trên)</span>
                  <strong>{formatNum(metrics.totalAsks, 2)} Coin</strong>
                </div>
                <div style={{ width: '100%', height: '12px', backgroundColor: 'rgba(239, 68, 68, 0.2)', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min((metrics.totalAsks / (metrics.totalAsks + metrics.totalBids || 1)) * 100, 100)}%`, height: '100%', backgroundColor: colors.red, transition: 'width 0.2s' }}></div>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: colors.green, marginBottom: '4px' }}>
                  <span>Tường Mua (Bids - Đỡ dưới)</span>
                  <strong>{formatNum(metrics.totalBids, 2)} Coin</strong>
                </div>
                <div style={{ width: '100%', height: '12px', backgroundColor: 'rgba(34, 197, 94, 0.2)', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min((metrics.totalBids / (metrics.totalAsks + metrics.totalBids || 1)) * 100, 100)}%`, height: '100%', backgroundColor: colors.green, transition: 'width 0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: colors.textMain, marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
              <span>⚔️ Lực Vã Market (CVD)</span>
              <span style={{ color: metrics.cvd > 0 ? colors.green : metrics.cvd < 0 ? colors.red : colors.textSub }}>
                {metrics.cvd > 0 ? 'Lực Gom' : metrics.cvd < 0 ? 'Lực Xả' : '--'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
              <strong style={{ fontSize: '32px', color: metrics.cvd > 0 ? colors.green : metrics.cvd < 0 ? colors.red : colors.textMain, transition: 'color 0.2s' }}>
                {metrics.cvd > 0 ? '+' : ''}{formatNum(metrics.cvd, 2)}
              </strong>
            </div>
          </div>
        </div>

        {/* ========================================= */}
        {/* KHU VỰC: BÀN CÂN DÒNG TIỀN THẬT / ẢO      */}
        {/* ========================================= */}
        <div style={{ backgroundColor: 'rgba(0,0,0,0.4)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', color: colors.textMain, fontWeight: 'bold' }}>
               ⚖️ BÀN CÂN DÒNG TIỀN THẬT/ẢO (CỘNG DỒN)
               <span style={{ fontSize: '11px', color: colors.textSub, marginLeft: '8px', fontWeight: 'normal' }}>*Lọc lệnh &gt; {formatUSD(Number(usdThreshold) || 0)}</span>
            </div>
            <button onClick={handleResetTow} style={{ background: 'transparent', border: `1px solid ${colors.border}`, color: colors.textSub, padding: '4px 10px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>
               🔄 Reset Bộ Đếm
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.05)', padding: '12px', borderRadius: '6px', border: `1px dashed ${colors.green}` }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.green, textAlign: 'center', marginBottom: '8px' }}>🚀 PHE LONG (LỰC ĐẨY TỔNG: {formatUSD(towMetrics.totalLongPower)})</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>
                    <span>🛡️ Gom Hàng (Bids Thật):</span>
                    <strong style={{ color: '#fff' }}>{formatUSD(towMetrics.bidsReal)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>
                    <span>🧊 Hứng Ẩn (Iceberg Long):</span>
                    <strong style={{ color: colors.green }}>{formatUSD(towMetrics.icebergLongs)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub }}>
                    <span>👻 Dọa Ma (Asks Ảo):</span>
                    <strong style={{ color: '#fff' }}>{formatUSD(towMetrics.asksFake)}</strong>
                </div>
            </div>

            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', padding: '12px', borderRadius: '6px', border: `1px dashed ${colors.red}` }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.red, textAlign: 'center', marginBottom: '8px' }}>🩸 PHE SHORT (LỰC ĐÈ TỔNG: {formatUSD(towMetrics.totalShortPower)})</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>
                    <span>🧱 Xả Hàng (Asks Thật):</span>
                    <strong style={{ color: '#fff' }}>{formatUSD(towMetrics.asksReal)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>
                    <span>🧊 Cản Ẩn (Iceberg Short):</span>
                    <strong style={{ color: colors.red }}>{formatUSD(towMetrics.icebergShorts)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub }}>
                    <span>👻 Đỡ Giả (Bids Ảo):</span>
                    <strong style={{ color: '#fff' }}>{formatUSD(towMetrics.bidsFake)}</strong>
                </div>
            </div>
          </div>

          <div style={{ marginTop: '16px', height: '14px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', overflow: 'hidden', display: 'flex' }}>
              <div style={{ 
                  width: `${towMetrics.totalLongPower + towMetrics.totalShortPower === 0 ? 50 : (towMetrics.totalLongPower / (towMetrics.totalLongPower + towMetrics.totalShortPower)) * 100}%`, 
                  height: '100%', backgroundColor: colors.green, transition: 'width 0.5s' 
              }}></div>
              <div style={{ flex: 1, height: '100%', backgroundColor: colors.red, transition: 'width 0.5s' }}></div>
          </div>

          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px dashed ${colors.border}` }}>
            <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '12px', fontWeight: 'bold' }}>
               🕵️ CHẨN ĐOÁN ĐÒN COMBO (MARKET MAKER):
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
              {MM_COMBOS.map(combo => {
                 const isActive = towMetrics.activeComboId === combo.id;
                 
                 let priceContextMsg = null;
                 if (isActive && towMetrics.baseP > 0) {
                     const trendIcon = towMetrics.currentPrice >= towMetrics.baseP ? '📈' : '📉';
                     const isSync = (combo.side === 'LONG' && towMetrics.currentPrice >= towMetrics.baseP) ||
                                    (combo.side === 'SHORT' && towMetrics.currentPrice < towMetrics.baseP);
                     const alignColor = isSync ? colors.green : colors.warn;
                     const syncText = isSync ? "Đồng Pha" : "Ngược Pha (Cẩn thận)";

                     priceContextMsg = (
                         <div style={{ marginTop: '6px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '10px', color: alignColor, fontWeight: 'bold' }}>
                             {trendIcon} Giá Live: ${formatNum(towMetrics.currentPrice, 4)} <br/>
                             🎯 Mốc: ${formatNum(towMetrics.baseP, 4)} ({towMetrics.priceDiff > 0 ? '+' : ''}{towMetrics.priceDiff.toFixed(2)}%)<br/>
                             ⚡ {syncText}
                         </div>
                     );
                 }

                 return (
                   <div key={combo.id} style={{
                      padding: '10px',
                      borderRadius: '6px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor: isActive ? combo.color : 'rgba(255,255,255,0.05)',
                      backgroundColor: isActive ? `${combo.color}20` : 'rgba(255,255,255,0.01)',
                      transition: 'background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease',
                      opacity: isActive ? 1 : 0.3,
                      boxSizing: 'border-box'
                   }}>
                     <div style={{ fontSize: '11px', fontWeight: 'bold', color: isActive ? combo.color : colors.textSub, display: 'flex', alignItems: 'center' }}>
                       <span style={{ width: '20px', display: 'inline-block', textAlign: 'left', opacity: isActive ? 1 : 0.5, transition: 'opacity 0.2s ease' }}>
                          {combo.icon}
                       </span>
                       <span>{combo.name}</span>
                     </div>
                     <div style={{ fontSize: '10px', color: isActive ? '#fff' : colors.textSub, marginTop: '4px', paddingLeft: '20px', lineHeight: '1.3' }}>
                       {combo.desc}
                       <div style={{ marginTop: '4px', fontSize: '9px', fontStyle: 'italic', color: isActive ? combo.color : colors.textSub, opacity: 0.8 }}>
                         {combo.formula}
                       </div>
                       {priceContextMsg}
                     </div>
                   </div>
                 )
              })}
            </div>
          </div>

        </div>

        {/* ========================================= */}
        {/* KHU VỰC 3 CỘT MA TRẬN CHI TIẾT */}
        {/* ========================================= */}
        <div style={{ marginTop: '10px', paddingTop: '20px', borderTop: `1px dashed ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', minHeight: '24px' }}>
              <div style={{ fontSize: '14px', color: colors.textMain, fontWeight: 'bold', textTransform: 'uppercase' }}>🔍 Ma trận Phân tích 3 Trục Hành Vi:</div>
              {metrics.overallScore > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', backgroundColor: 'rgba(0,0,0,0.3)', padding: '4px 12px', borderRadius: '12px' }}>
                      <span style={{ color: colors.textSub }}>Tín hiệu Chủ Đạo:</span>
                      <strong style={{ color: metrics.overallSide === 'DUMP' ? colors.red : colors.green }}>
                         {metrics.overallSide === 'DUMP' ? '📉 DUMP' : '📈 PUMP'} ({metrics.overallScore}%)
                      </strong>
                  </div>
              )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
             {renderCategoryColumn("Ý ĐỊNH (Sổ Lệnh)", "👁️", "INTENT")}
             {renderCategoryColumn("HÀNH ĐỘNG (CVD)", "💸", "ACTION")}
             {renderCategoryColumn("XÁC NHẬN (Cấu Trúc)", "🎯", "CONFIRMATION")}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {renderHistoryBox(
                "👁️ NHẬT KÝ Ý ĐỊNH SỔ LỆNH (Dọa ma / Đặt bẫy):", 
                intentHistory, 
                "Đang đợi cá mập giăng bẫy..."
            )}
            
            {renderHistoryBox(
                "⚔️ NHẬT KÝ HÀNH ĐỘNG THỰC TẾ & XÁC NHẬN:", 
                actionHistory, 
                "Chưa có tín hiệu bơm/xả thực tế...",
                actionComparisonUI
            )}
          </div>
          
        </div>

      </div>
    </div>
  );
});
export default RealtimeDumpRadar;
